import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import slugify from 'slugify';
import { doctorSchema, type DoctorFormData } from '@/shared/schemas';
import { SubmitButton } from '@/shared/components';

import type { DoctorWithImages } from '@/shared/types';

/**
 * Пропсы для компонента DoctorForm
 */
interface DoctorFormProps {
  doctor?: DoctorWithImages;
  onSubmit: (data: DoctorFormData) => Promise<void>;
  isLoading?: boolean;
  submitText?: string;
}

/**
 * Компонент формы врача
 * Используется для создания и редактирования врачей
 * @param doctor - Данные врача для редактирования (опционально)
 * @param onSubmit - Функция отправки формы
 * @param isLoading - Состояние загрузки
 * @param submitText - Текст кнопки отправки
 * @returns JSX элемент
 */
export const DoctorForm: React.FC<DoctorFormProps> = ({
  doctor,
  onSubmit,
  isLoading = false,
  submitText = 'Сохранить'
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<DoctorFormData>({
    resolver: zodResolver(doctorSchema),
    defaultValues: doctor
      ? {
          slug: doctor.slug || '',
          fullName: doctor.fullName,
          specialization: doctor.specialization,
          description: doctor.description,
          educationPrimary: doctor.educationPrimary,
          educationAdvanced: doctor.educationAdvanced || '',
          interestsArea: doctor.interestsArea || '',
          principles: doctor.principles || '',
          experience: doctor.experience || '',
          practiceSinceYear: doctor.practiceSinceYear ?? null,
          onSiteBookingUrl: doctor.onSiteBookingUrl || '',
          proDoctorovReviewsWidgetCode: doctor.proDoctorovReviewsWidgetCode || '',
          isActive: doctor.isActive,
          isAvailableOnline: doctor.isAvailableOnline,
          isAvailableAtHome: doctor.isAvailableAtHome,
          isAvailableInTheOffice: doctor.isAvailableInTheOffice,
          sortOrder: doctor.sortOrder
        }
      : {
          slug: '',
          fullName: '',
          specialization: '',
          description: '',
          educationPrimary: '',
          educationAdvanced: '',
          interestsArea: '',
          principles: '',
          experience: '',
          practiceSinceYear: null,
          onSiteBookingUrl: '',
          proDoctorovReviewsWidgetCode: '',
          isActive: true,
          isAvailableOnline: false,
          isAvailableAtHome: false,
          isAvailableInTheOffice: false,
          sortOrder: 0
        }
  });

  const fullName = watch('fullName');

  /**
   * Автоматическая генерация slug при изменении имени
   */
  useEffect(() => {
    if (fullName && !doctor) {
      const generatedSlug = slugify(fullName, {
        lower: true,
        strict: true,
        locale: 'ru'
      });
      setValue('slug', generatedSlug);
    }
  }, [fullName, setValue, doctor]);

  /**
   * Обработчик отправки формы
   */
  const handleFormSubmit = async (data: DoctorFormData): Promise<void> => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Основная информация */}
      <div className="card card-bordered bg-base-100 shadow-sm">
        <div className="card-body p-2">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            <i className="fas fa-user mr-2 text-primary"></i>
            Основная информация
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Полное имя */}
            <div className="form-control md:col-span-2">
              <label className="label">
                <span className="label-text">Полное имя *</span>
              </label>
              <input
                {...register('fullName')}
                type="text"
                placeholder="Иванов Иван Иванович"
                className={`input input-bordered w-full ${errors.fullName ? 'input-error' : ''}`}
                disabled={isLoading || isSubmitting}
              />
              {errors.fullName && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.fullName.message}
                  </span>
                </label>
              )}
            </div>

            {/* Slug */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Slug (URL)</span>
                <span className="label-text-alt">
                  Автоматически генерируется
                </span>
              </label>
              <input
                {...register('slug')}
                type="text"
                placeholder="ivanov-ivan-ivanovich"
                className={`input input-bordered w-full ${errors.slug ? 'input-error' : ''}`}
                disabled
              />
              {errors.slug && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.slug.message}
                  </span>
                </label>
              )}
            </div>

            {/* Специализация */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Специализация *</span>
              </label>
              <input
                {...register('specialization')}
                type="text"
                placeholder="Педиатр"
                className={`input input-bordered w-full ${errors.specialization ? 'input-error' : ''}`}
                disabled={isLoading || isSubmitting}
              />
              {errors.specialization && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.specialization.message}
                  </span>
                </label>
              )}
            </div>

            {/* URL для записи в клинике */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">URL для записи в клинике</span>
                <span className="label-text-alt">Опционально</span>
              </label>
              <input
                {...register('onSiteBookingUrl')}
                type="url"
                placeholder="https://example.com/booking"
                className={`input input-bordered w-full ${errors.onSiteBookingUrl ? 'input-error' : ''}`}
                disabled={isLoading || isSubmitting}
              />
              {errors.onSiteBookingUrl && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.onSiteBookingUrl.message}
                  </span>
                </label>
              )}
            </div>

            {/* Описание */}
            <div className="form-control md:col-span-2">
              <label className="label">
                <span className="label-text">Описание *</span>
              </label>
              <textarea
                {...register('description')}
                placeholder="Краткое описание врача..."
                rows={4}
                className={`textarea textarea-bordered w-full ${errors.description ? 'textarea-error' : ''}`}
                disabled={isLoading || isSubmitting}
              />
              {errors.description && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.description.message}
                  </span>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Образование и опыт */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              <i className="fas fa-graduation-cap mr-2 text-primary"></i>
              Образование и опыт
            </h3>

            <div className="space-y-4">
              {/* Основное образование */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Основное образование *</span>
                </label>
                <textarea
                  {...register('educationPrimary')}
                  placeholder="Основное медицинское образование..."
                  rows={3}
                  className={`textarea textarea-bordered w-full ${errors.educationPrimary ? 'textarea-error' : ''}`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.educationPrimary && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.educationPrimary.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Дополнительное образование */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    Дополнительное образование
                  </span>
                </label>
                <textarea
                  {...register('educationAdvanced')}
                  placeholder="Курсы повышения квалификации, сертификаты..."
                  rows={3}
                  className={`textarea textarea-bordered w-full ${errors.educationAdvanced ? 'textarea-error' : ''}`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.educationAdvanced && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.educationAdvanced.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Области интересов */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Области интересов</span>
                </label>
                <textarea
                  {...register('interestsArea')}
                  placeholder="Специализации и области интересов..."
                  rows={3}
                  className={`textarea textarea-bordered w-full ${errors.interestsArea ? 'textarea-error' : ''}`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.interestsArea && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.interestsArea.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Принципы работы */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Принципы работы</span>
                </label>
                <textarea
                  {...register('principles')}
                  placeholder="Принципы и подходы к работе с пациентами..."
                  rows={3}
                  className={`textarea textarea-bordered w-full ${errors.principles ? 'textarea-error' : ''}`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.principles && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.principles.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Опыт работы */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Опыт работы</span>
                </label>
                <textarea
                  {...register('experience')}
                  placeholder="Опыт работы и достижения..."
                  rows={3}
                  className={`textarea textarea-bordered w-full ${errors.experience ? 'textarea-error' : ''}`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.experience && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.experience.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Год начала практики — из него на сайте считается стаж */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Практикует с (год)</span>
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  {...register('practiceSinceYear')}
                  placeholder="Например, 2014"
                  min={1950}
                  max={2100}
                  className={`input input-bordered w-full ${errors.practiceSinceYear ? 'input-error' : ''}`}
                  disabled={isLoading || isSubmitting}
                />
                <label className="label">
                  <span className="label-text-alt opacity-70">
                    На карточке врача появится «Стаж N лет». Считается от этого
                    года, поэтому обновлять каждый январь не нужно. Оставьте
                    пустым — плашки не будет.
                  </span>
                </label>
                {errors.practiceSinceYear && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.practiceSinceYear.message}
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Настройки */}
      <div className="card card-bordered bg-base-100 shadow-sm">
        <div className="card-body p-2">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            <i className="fas fa-cog mr-2 text-primary"></i>
            Настройки
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Порядок сортировки */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Порядок сортировки</span>
                <span className="label-text-alt">
                  Изменяется перетаскиванием в списке
                </span>
              </label>
              <input
                {...register('sortOrder', { valueAsNumber: true })}
                type="number"
                min="0"
                placeholder="0"
                className={`input input-bordered w-full ${errors.sortOrder ? 'input-error' : ''}`}
                disabled={true}
                title="Порядок сортировки изменяется перетаскиванием врачей в списке"
              />
              {errors.sortOrder && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.sortOrder.message}
                  </span>
                </label>
              )}
            </div>

            {/* Чекбоксы */}
            <div className="space-y-3">
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    {...register('isActive')}
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    disabled={isLoading || isSubmitting}
                  />
                  <span className="label-text">Активен</span>
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    {...register('isAvailableOnline')}
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    disabled={isLoading || isSubmitting}
                  />
                  <span className="label-text">Доступен онлайн</span>
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    {...register('isAvailableAtHome')}
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    disabled={isLoading || isSubmitting}
                  />
                  <span className="label-text">Выезд на дом</span>
                </label>
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    {...register('isAvailableInTheOffice')}
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    disabled={isLoading || isSubmitting}
                  />
                  <span className="label-text">Прием в клинике</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Виджет отзывов */}
      <div className="card card-bordered bg-base-100 shadow-sm">
        <div className="card-body p-2">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            <i className="fas fa-star mr-2 text-primary"></i>
            Виджет отзывов
          </h3>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Код виджета ProDoctorov</span>
              <span className="label-text-alt">Опционально</span>
            </label>
            <textarea
              {...register('proDoctorovReviewsWidgetCode')}
              placeholder="Вставьте HTML/JS код виджета отзывов..."
              rows={6}
              className={`textarea textarea-bordered w-full font-mono text-sm ${errors.proDoctorovReviewsWidgetCode ? 'textarea-error' : ''}`}
              disabled={isLoading || isSubmitting}
            />
            {errors.proDoctorovReviewsWidgetCode && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.proDoctorovReviewsWidgetCode.message}
                </span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="flex gap-4">
        <SubmitButton
          isLoading={isLoading || isSubmitting}
          loadingText="Сохранение..."
          fullWidth
        >
          {submitText}
        </SubmitButton>
      </div>
    </form>
  );
};
